/* ==========================================================================
   U Press artwork preflight  -  assets/up-preflight.js
   --------------------------------------------------------------------------
   Adds an ADVISORY "possible white background" check alongside the Build a
   Gang Sheet (BAGS) upload component.

   BAGS is never modified, patched or reimplemented. This module only:
     * reads the File the customer already handed to BAGS' own <input>,
     * analyses it locally in the browser (no upload, no network),
     * inserts one panel between BAGS' size/DPI result and its Add to Cart,
     * blocks OUR gate (a capture-phase click) until the customer
       acknowledges a warning.

   BAGS remains authoritative for upload, size detection, DPI, variant,
   price, quantity, Add to Cart and cart metadata.

   Structure (deliberately separable - BAGS is temporary infrastructure):

     Detector  File|Blob -> {status, reason, ms, metrics}
               Pure. No DOM, no BAGS, no network. Callable directly as
               window.upPreflight.detect(file) by any future uploader.
     Panel     Renders the four customer states, owns the acknowledgement.
               Knows nothing about where the file came from.
     Adapter   The ONLY part that knows BAGS exists. All of its knowledge is
               the four selectors in the BAGS object below; replacing BAGS
               means replacing that object and the small sync/place helpers.

   The gate is assistive, matching BAGS' own low-DPI acknowledgement. It
   covers the ordinary customer paths (pointer, keyboard, form submit); it is
   not a security boundary and does not pretend to be one.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG_EL = document.getElementById('up-preflight-config');
  if (!CONFIG_EL) { try { console.warn('[up-preflight] config element missing; not starting'); } catch (e) {} return; }

  var USER = {};
  try { USER = JSON.parse(CONFIG_EL.textContent || '{}') || {}; } catch (e) { USER = {}; }
  if (USER.enabled === false) return;

  /* ======================================================================
     Tunables. Everything the detector decides with lives here - no magic
     numbers further down. Any of these can be overridden per template from
     the snippet's `config` setting.
     ====================================================================== */
  var T = {
    // how much of the artwork we are willing to hold in memory at once
    maxInspectionPixels: 900000,   // ~0.9 MP working copy
    maxSourcePixels: 400000000,    // refuse to decode beyond this (400 MP)

    // pixel classification
    alphaOpaque: 250,              // >= this alpha counts as opaque
    alphaTransparent: 8,           // <= this alpha counts as transparent
    whiteMinChannel: 243,          // all of r,g,b >= this
    whiteMaxChroma: 12,            // max(r,g,b) - min(r,g,b) <= this

    // a near-white region has to be this substantial before we judge it
    minRegionAreaFrac: 0.004,      // 0.4% of the inspected sheet
    minRegionAreaPx: 400,
    minRegionDim: 22,              // ignore thin strokes

    // what makes a near-white region look like a pasted-in white canvas:
    // its bounding box is almost entirely opaque (white plus the art sitting
    // on it) while the sheet around it is transparent
    bboxOpaqueMin: 0.93,
    bboxWhiteMin: 0.35,
    ringTransparentMin: 0.40,
    ringWidth: 6,

    // A sheet with no transparency is NOT automatically a white background:
    // a photograph or a full-bleed coloured design is legitimate. We only call
    // it a white canvas when the sheet is dominantly near-white AND its outer
    // band is near-white, i.e. a white field surrounding the artwork.
    flatCanvasOpaqueFrac: 0.985,   // essentially no transparency anywhere
    flatCanvasWhiteMin: 0.50,      // half the sheet is near-white
    flatCanvasBorderWhiteMin: 0.80,// its outer band is near-white
    borderBandFrac: 0.06,          // outer 6% counts as the border band

    // how many suspicious regions to keep for a future "here is where" preview
    maxReportedRegions: 12,

    maxRegions: 6000,

    /* Transparency review. A fully opaque sheet is 0.33% of the production
       archive (19 of 5,739 measured), so this is rare and worth showing. */
    opaqueSheetWarning: true
  };
  if (USER.thresholds) {
    for (var k in USER.thresholds) {
      if (Object.prototype.hasOwnProperty.call(T, k)) T[k] = USER.thresholds[k];
    }
  }

  var COPY = {
    title: 'Artwork check',
    checking: 'Checking artwork…',
    passTitle: 'No obvious background detected',
    passBody: 'Automated preflight complete. Please still review your artwork before ordering.',
    warnTitle: 'Possible background detected',
    warnBody: 'We found an area in your artwork that may contain an unwanted white background. ' +
              'White areas in your file may print as part of your transfer. Please review your ' +
              'artwork before continuing.',
    unableTitle: 'We couldn’t automatically check this file',
    unableBody: 'We couldn’t automatically check this file for white backgrounds. Please review ' +
                'your artwork carefully before continuing.',
    ack: 'I’ve reviewed my artwork and want to continue as uploaded.',
    blocked: 'Please review the artwork check above and tick the box to continue.',
    actPrintTitle: 'Print as uploaded',
    actPrintBody: 'U Press will print exactly what I submitted.',
    actReviewTitle: 'Review the areas',
    actReviewBody: 'See exactly where we found something, then decide.',
    actFixTitle: 'I’ll fix it myself',
    actFixBody: 'I’ll correct my file and upload it again.',
    printAck: 'I reviewed the artwork warning and want this file printed exactly as uploaded.',
    fixHelp: 'Remove or replace your current file after making your corrections. ' +
             'U Press will automatically check the new file again.',
    transparencyTitle: 'Transparency review recommended',
    transparencyBody: 'Your uploaded sheet does not appear to contain a transparent ' +
      'background. If this is intentional, you can continue. If not, the entire ' +
      'rectangular sheet may print.',
    transparencyAck: 'This is intentional — continue.',
    areasOne: '1 area needs review.',
    areasMany: '{n} areas need review.'
  };

  /* The snippet publishes wording in its config blob and documents that you can
     override it per template - but nothing here ever read it, so editing the
     snippet changed nothing on the page. Merge it over the defaults so the
     documented overrides actually work. */
  if (USER.copy) {
    Object.keys(USER.copy).forEach(function (k) {
      if (typeof USER.copy[k] === 'string' && USER.copy[k]) COPY[k] = USER.copy[k];
    });
  }

  /* repair policy removed with the repair flow; see the Artwork Lab archive */

  var INSPECTABLE = { 'image/png': 1, 'image/webp': 1 };
  var INSPECTABLE_EXT = { png: 1, webp: 1 };

  function log() {
    if (!USER.debug) return;
    try { console.log.apply(console, ['[up-preflight]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  /* ======================================================================
     1. DETECTOR
     ====================================================================== */

  function extOf(name) {
    var m = /\.([a-z0-9]+)\s*$/i.exec(name || '');
    return m ? m[1].toLowerCase() : '';
  }

  function isInspectable(file) {
    if (!file) return false;
    if (file.type && INSPECTABLE[file.type.toLowerCase()]) return true;
    if (!file.type && INSPECTABLE_EXT[extOf(file.name)]) return true;
    // some browsers report image/png for .png only; trust the extension too
    return !!INSPECTABLE_EXT[extOf(file.name)] && !/pdf|psd|postscript|svg|illustrator/i.test(file.type || '');
  }

  /* Read the PNG IHDR so we know the pixel count BEFORE asking the browser to
     decode it. Lets us bail out of absurd files instead of risking the tab. */
  function readPngSize(file) {
    return file.slice(0, 33).arrayBuffer().then(function (buf) {
      var b = new Uint8Array(buf);
      if (b.length < 24) return null;
      var sig = [137, 80, 78, 71, 13, 10, 26, 10];
      for (var i = 0; i < 8; i++) { if (b[i] !== sig[i]) return null; }
      var dv = new DataView(buf);
      return { width: dv.getUint32(16), height: dv.getUint32(20) };
    }).catch(function () { return null; });
  }

  function fitInside(w, h, maxPixels) {
    var scale = Math.sqrt(maxPixels / (w * h));
    if (scale >= 1) return { w: w, h: h };
    return {
      w: Math.max(1, Math.round(w * scale)),
      h: Math.max(1, Math.round(h * scale))
    };
  }

  function getImageData(file) {
    if (typeof createImageBitmap !== 'function') {
      return Promise.reject(new Error('no createImageBitmap'));
    }
    var trueSize = null;   // dimensions of the ORIGINAL file, not the working copy
    return readPngSize(file).then(function (size) {
      if (size && size.width * size.height > T.maxSourcePixels) {
        throw new Error('source too large: ' + size.width + 'x' + size.height);
      }
      var opts = {
        premultiplyAlpha: 'default',
        colorSpaceConversion: 'none',
        resizeQuality: 'medium'
      };
      if (size) {
        var t = fitInside(size.width, size.height, T.maxInspectionPixels);
        opts.resizeWidth = t.w;
        opts.resizeHeight = t.h;
      }
      trueSize = size;
      return createImageBitmap(file, opts);
    }).then(function (bmp) {
      var w = bmp.width, h = bmp.height;
      // if we could not read a header, downscale on the canvas instead
      var t = fitInside(w, h, T.maxInspectionPixels);
      var canvas = (typeof OffscreenCanvas === 'function')
        ? new OffscreenCanvas(t.w, t.h)
        : (function () { var c = document.createElement('canvas'); c.width = t.w; c.height = t.h; return c; })();
      var ctx = canvas.getContext('2d', { alpha: true, willReadFrequently: true });
      if (!ctx) { bmp.close && bmp.close(); throw new Error('no 2d context'); }
      ctx.clearRect(0, 0, t.w, t.h);
      ctx.drawImage(bmp, 0, 0, t.w, t.h);
      bmp.close && bmp.close();
      var data = ctx.getImageData(0, 0, t.w, t.h);
      return {
        data: data,
        sourceW: trueSize ? trueSize.width : w,
        sourceH: trueSize ? trueSize.height : h
      };
    });
  }

  /* Classify every pixel, then judge each connected NEAR-WHITE region by the
     box it occupies.

     The thing we are hunting is a placed design that still carries its own
     rectangular white canvas. The giveaway is not the white itself - white
     artwork is perfectly legitimate - it is that the white region's bounding
     box is almost completely OPAQUE (the white canvas, plus whatever art sits
     on top of it) while the gang sheet around that box is transparent.

     Genuine white artwork fails that test: the box around a white letter or a
     white star is mostly empty sheet, so its opaque coverage is low.

     Judging the white region's box (rather than the opaque island it belongs
     to) also survives the common real-world case where the offending design
     touches a neighbouring design - they merge into one opaque island, but the
     white area is still bounded by its own rectangle.

     Box coverage is answered in O(1) per region from two summed-area tables,
     so this stays linear in the inspected pixel count. */
  function analyse(imgData) {
    var d = imgData.data.data, W = imgData.data.width, H = imgData.data.height;
    var n = W * H;
    var TRANSPARENT = 0, INK = 1, WHITE = 2;
    var cls = new Uint8Array(n);
    var opaqueCount = 0, whiteCount = 0, transparentCount = 0;

    for (var i = 0, p = 0; i < n; i++, p += 4) {
      var a = d[p + 3];
      if (a <= T.alphaTransparent) { cls[i] = TRANSPARENT; transparentCount++; continue; }
      if (a < T.alphaOpaque) { cls[i] = TRANSPARENT; continue; }   // blended edge: ignore
      var r = d[p], g = d[p + 1], b = d[p + 2];
      var mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
      var mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
      opaqueCount++;
      if (mn >= T.whiteMinChannel && (mx - mn) <= T.whiteMaxChroma) { cls[i] = WHITE; whiteCount++; }
      else { cls[i] = INK; }
    }

    var metrics = {
      inspected: W + 'x' + H,
      source: imgData.sourceW + 'x' + imgData.sourceH,
      opaqueFrac: +(opaqueCount / n).toFixed(4),
      whiteFrac: +(whiteCount / n).toFixed(4),
      transparentFrac: +(transparentCount / n).toFixed(4),
      regions: 0,
      worst: null
    };

    /* A sheet with no transparency is not automatically a problem - a
       photograph or a deliberate full-bleed design is legitimate artwork. It
       only looks like a flattened WHITE canvas when the sheet is dominantly
       near-white AND its outer band is near-white, which is what a white
       background around a design actually looks like. A colourful photo or a
       solid dark background fails the border test and passes silently. */
    if (metrics.opaqueFrac >= T.flatCanvasOpaqueFrac) {
      var band = Math.max(1, Math.round(Math.min(W, H) * T.borderBandFrac));
      var bandWhite = 0, bandTotal = 0;
      for (var by = 0; by < H; by++) {
        var edgeRow = (by < band || by >= H - band);
        for (var bx = 0; bx < W; bx++) {
          if (!edgeRow && bx >= band && bx < W - band) { bx = W - band - 1; continue; }
          bandTotal++;
          if (cls[by * W + bx] === WHITE) bandWhite++;
        }
      }
      var borderWhite = bandTotal ? bandWhite / bandTotal : 0;
      var whiteOfOpaque = whiteCount / Math.max(1, opaqueCount);
      metrics.opaque = {
        whiteOfOpaque: +whiteOfOpaque.toFixed(3),
        borderWhite: +borderWhite.toFixed(3)
      };
      metrics.sourceW = imgData.sourceW;
      metrics.sourceH = imgData.sourceH;
      metrics.scale = imgData.sourceW / W;
      metrics.regionsSource = [{ n: 1, kind: 'whole-sheet',
        box: [0, 0, imgData.sourceW, imgData.sourceH] }];
      if (whiteOfOpaque >= T.flatCanvasWhiteMin && borderWhite >= T.flatCanvasBorderWhiteMin) {
        metrics.regionsReported = [{ box: [0, 0, W, H], kind: 'flat-white-canvas' }];
        return { status: 'warn', reason: 'flat-white-canvas', metrics: metrics };
      }
      /* Not a white field, but the sheet has no transparency anywhere. On a
         gang sheet that means the whole rectangle prints as a solid block. It
         may be deliberate, so this is a review prompt, never a verdict. */
      if (T.opaqueSheetWarning) {
        metrics.regionsReported = [{ box: [0, 0, W, H], kind: 'opaque-sheet' }];
        return { status: 'warn', reason: 'opaque-sheet', metrics: metrics };
      }
      return { status: 'pass', reason: 'opaque-not-white', metrics: metrics };
    }
    if (whiteCount === 0) return { status: 'pass', reason: 'no-white', metrics: metrics };

    // summed-area tables for O(1) box queries
    var sw = W + 1;
    var intOpaque = new Int32Array(sw * (H + 1));
    var intWhite = new Int32Array(sw * (H + 1));
    for (var y = 0; y < H; y++) {
      var rowO = 0, rowW = 0, base = y * W, ibase = (y + 1) * sw;
      for (var x = 0; x < W; x++) {
        var c = cls[base + x];
        if (c !== TRANSPARENT) rowO++;
        if (c === WHITE) rowW++;
        intOpaque[ibase + x + 1] = intOpaque[ibase - sw + x + 1] + rowO;
        intWhite[ibase + x + 1] = intWhite[ibase - sw + x + 1] + rowW;
      }
    }
    function boxSum(tbl, x0, y0, x1, y1) {   // inclusive coords
      return tbl[(y1 + 1) * sw + (x1 + 1)] - tbl[y0 * sw + (x1 + 1)] -
             tbl[(y1 + 1) * sw + x0] + tbl[y0 * sw + x0];
    }

    // connected components over the NEAR-WHITE mask
    var labels = new Int32Array(n);
    var stack = new Int32Array(n);
    var minArea = Math.max(T.minRegionAreaPx, Math.floor(n * T.minRegionAreaFrac));
    var label = 0, worst = null, suspicious = [];

    for (var s0 = 0; s0 < n; s0++) {
      if (labels[s0] !== 0 || cls[s0] !== WHITE) continue;
      label++;
      if (label > T.maxRegions) break;
      var top = 0;
      stack[top++] = s0; labels[s0] = label;
      var area = 0, x0 = W, y0 = H, x1 = -1, y1 = -1;
      while (top > 0) {
        var q = stack[--top];
        var qx = q % W, qy = (q - qx) / W;
        area++;
        if (qx < x0) x0 = qx;
        if (qx > x1) x1 = qx;
        if (qy < y0) y0 = qy;
        if (qy > y1) y1 = qy;
        if (qx > 0)     { var l = q - 1; if (cls[l] === WHITE && labels[l] === 0) { labels[l] = label; stack[top++] = l; } }
        if (qx < W - 1) { var rr = q + 1; if (cls[rr] === WHITE && labels[rr] === 0) { labels[rr] = label; stack[top++] = rr; } }
        if (qy > 0)     { var u = q - W; if (cls[u] === WHITE && labels[u] === 0) { labels[u] = label; stack[top++] = u; } }
        if (qy < H - 1) { var dn = q + W; if (cls[dn] === WHITE && labels[dn] === 0) { labels[dn] = label; stack[top++] = dn; } }
      }

      var bw = x1 - x0 + 1, bh = y1 - y0 + 1;
      if (area < minArea || bw < T.minRegionDim || bh < T.minRegionDim) continue;

      var boxArea = bw * bh;
      var opaqueIn = boxSum(intOpaque, x0, y0, x1, y1);
      var whiteIn = boxSum(intWhite, x0, y0, x1, y1);

      // transparency in a ring just outside the box: is this an island?
      var rw = T.ringWidth;
      var rx0 = Math.max(0, x0 - rw), ry0 = Math.max(0, y0 - rw);
      var rx1 = Math.min(W - 1, x1 + rw), ry1 = Math.min(H - 1, y1 + rw);
      var ringArea = (rx1 - rx0 + 1) * (ry1 - ry0 + 1) - boxArea;
      var ringOpaque = boxSum(intOpaque, rx0, ry0, rx1, ry1) - opaqueIn;
      var ringTransparent = ringArea > 0 ? (ringArea - ringOpaque) / ringArea : 0;

      var cand = {
        areaFrac: +(area / n).toFixed(4),
        box: [x0, y0, bw, bh],
        bboxOpaque: +(opaqueIn / boxArea).toFixed(3),
        bboxWhite: +(whiteIn / boxArea).toFixed(3),
        ringTransparent: +ringTransparent.toFixed(3)
      };
      cand.suspicious = cand.bboxOpaque >= T.bboxOpaqueMin &&
                        cand.bboxWhite >= T.bboxWhiteMin &&
                        cand.ringTransparent >= T.ringTransparentMin;

      if (cand.suspicious && suspicious.length < T.maxReportedRegions) suspicious.push(cand);
      if (!worst || (cand.suspicious && !worst.suspicious) ||
          (cand.suspicious === worst.suspicious && cand.areaFrac > worst.areaFrac)) {
        worst = cand;
      }
    }

    metrics.regions = label;
    metrics.worst = worst;
    /* Boxes are in INSPECTED pixel space; multiply by scale for source pixels.
       Retained so a later "here is where we found it" preview needs no
       re-analysis. Nothing in the UI reads this today. */
    metrics.regionsReported = suspicious;
    metrics.scale = imgData.sourceW / W;
    metrics.sourceW = imgData.sourceW;
    metrics.sourceH = imgData.sourceH;
    /* The review surface draws on the real sheet, so hand it boxes in SOURCE
       pixels with a 1-based number the customer sees as "Area 1". */
    metrics.regionsSource = suspicious.map(function (r, i) {
      var k = metrics.scale;
      return {
        n: i + 1,
        kind: 'background',
        box: [Math.round(r.box[0] * k), Math.round(r.box[1] * k),
              Math.round(r.box[2] * k), Math.round(r.box[3] * k)]
      };
    });
    if (worst && worst.suspicious) {
      return { status: 'warn', reason: 'white-canvas-box', metrics: metrics };
    }
    return { status: 'pass', reason: 'no-suspicious-region', metrics: metrics };
  }

  function detect(file) {
    var started = (window.performance && performance.now) ? performance.now() : Date.now();
    if (!isInspectable(file)) {
      return Promise.resolve({ status: 'unable', reason: 'format:' + (file && (file.type || extOf(file.name))), ms: 0 });
    }
    return getImageData(file).then(function (img) {
      var res = analyse(img);
      res.ms = Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - started);
      return res;
    }).catch(function (err) {
      return {
        status: 'unable',
        reason: 'decode:' + (err && err.message ? err.message : 'failed'),
        ms: Math.round(((window.performance && performance.now) ? performance.now() : Date.now()) - started)
      };
    });
  }

  /* ======================================================================
     2. PANEL
     ====================================================================== */

  var ICONS = {
    pass: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M8.2 12.3l2.6 2.6 5-5.4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    warn: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.6l9 15.6H3z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M12 9.6v4.2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="12" cy="16.8" r="1.1" fill="currentColor"/></svg>',
    unable: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M9.6 9.4a2.5 2.5 0 113.2 2.4c-.6.2-.9.7-.9 1.3v.4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="16.6" r="1" fill="currentColor"/></svg>',
    checking: '<svg viewBox="0 0 24 24" aria-hidden="true" class="up-pf__spin"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-dasharray="40 14"/></svg>'
  };

  function Panel() {
    var el = document.createElement('div');
    el.className = 'up up-pf';
    el.setAttribute('data-up-preflight', '');
    el.innerHTML =
      '<div class="up-pf__row">' +
        '<span class="up-pf__icon" data-pf-icon></span>' +
        '<div class="up-pf__text">' +
          '<span class="up-pf__eyebrow">' + COPY.title + '</span>' +
          '<span class="up-pf__title" data-pf-title></span>' +
          '<p class="up-pf__body" data-pf-body></p>' +
        '</div>' +
      '</div>' +
      '<div class="up-pf__actions" data-pf-actions hidden>' +
        '<button type="button" class="up-pf__act up-pf__act--primary" data-pf-choice="review">' +
          '<b>' + COPY.actReviewTitle + '</b><span>' + COPY.actReviewBody + '</span></button>' +
        '<button type="button" class="up-pf__act" data-pf-choice="print">' +
          '<b>' + COPY.actPrintTitle + '</b><span>' + COPY.actPrintBody + '</span></button>' +
        '<button type="button" class="up-pf__act" data-pf-choice="fix">' +
          '<b>' + COPY.actFixTitle + '</b><span>' + COPY.actFixBody + '</span></button>' +
      '</div>' +
      '<p class="up-pf__body" data-pf-fixhelp hidden>' + COPY.fixHelp + '</p>' +
      '<label class="up-pf__ack" data-pf-ack hidden>' +
        '<input type="checkbox" data-pf-ack-input>' +
        '<span data-pf-ack-label>' + COPY.ack + '</span>' +
      '</label>' +
      '<p class="up-pf__nudge" data-pf-nudge hidden>' + COPY.blocked + '</p>';

    this.el = el;
    this.iconEl = el.querySelector('[data-pf-icon]');
    this.titleEl = el.querySelector('[data-pf-title]');
    this.bodyEl = el.querySelector('[data-pf-body]');
    this.ackEl = el.querySelector('[data-pf-ack]');
    this.ackInput = el.querySelector('[data-pf-ack-input]');
    this.ackLabel = el.querySelector('[data-pf-ack-label]');
    this.actionsEl = el.querySelector('[data-pf-actions]');
    this.fixHelpEl = el.querySelector('[data-pf-fixhelp]');
    this.choice = null;
    this.repaired = false;
    this.nudgeEl = el.querySelector('[data-pf-nudge]');
    this.state = 'checking';

    var self = this;
    this.ackInput.addEventListener('change', function () {
      self.el.classList.toggle('is-acknowledged', self.ackInput.checked);
      if (self.ackInput.checked) self.nudgeEl.hidden = true;
    });
  }

  Panel.prototype.set = function (state, detail) {
    this.state = state;
    this.el.setAttribute('data-state', state);
    this.nudgeEl.hidden = true;
    if (state === 'checking') {
      this.iconEl.innerHTML = ICONS.checking;
      this.titleEl.textContent = COPY.checking;
      this.bodyEl.textContent = '';
      this.bodyEl.hidden = true;
      this.ackEl.hidden = true;
      this.actionsEl.hidden = true;
      this.fixHelpEl.hidden = true;
    } else if (state === 'pass') {
      this.iconEl.innerHTML = ICONS.pass;
      this.titleEl.textContent = COPY.passTitle;
      this.bodyEl.textContent = COPY.passBody;
      this.bodyEl.hidden = false;
      this.ackEl.hidden = true;
      this.actionsEl.hidden = true;
      this.fixHelpEl.hidden = true;
    } else if (state === 'warn') {
      this.iconEl.innerHTML = ICONS.warn;
      this.reason = detail || null;
      this.transparency = (detail === 'opaque-sheet' || detail === 'transparency');
      this.titleEl.textContent = this.transparency
        ? COPY.transparencyTitle : COPY.warnTitle;
      /* A fully opaque sheet is an anomaly worth a look, not a fault. It is
         phrased as a recommendation and the acknowledgement says "intentional",
         never "I accept this problem". */
      var n = this.areaCount || 0;
      if (this.transparency) {
        this.bodyEl.textContent = COPY.transparencyBody;
      } else {
        this.bodyEl.textContent = n > 0
          ? (n === 1 ? COPY.areasOne : COPY.areasMany.replace('{n}', n)) + ' ' + COPY.warnBody
          : COPY.warnBody;
      }
      this.bodyEl.hidden = false;
      // choices first; the acknowledgement only appears for "print as-is"
      this.actionsEl.hidden = false;
      this.ackEl.hidden = this.choice !== 'print';
      this.fixHelpEl.hidden = this.choice !== 'fix';
    } else {
      this.iconEl.innerHTML = ICONS.unable;
      this.titleEl.textContent = COPY.unableTitle;
      this.bodyEl.textContent = COPY.unableBody;
      this.bodyEl.hidden = false;
      this.actionsEl.hidden = true;      // nothing to show if we cannot read it
      this.fixHelpEl.hidden = true;
      this.ackEl.hidden = false;
    }
    if (detail) this.el.setAttribute('data-reason', detail);
  };

  Panel.prototype.needsAck = function () {
    if (this.state !== 'warn' && this.state !== 'unable') return false;
    return !this.ackInput.checked;
  };

  Panel.prototype.reset = function () {
    this.ackInput.checked = false;
    this.el.classList.remove('is-acknowledged');
    this.nudgeEl.hidden = true;
    this.choice = null;
    this.repaired = false;
    this.areaCount = 0;
    this.reason = null;
    this.transparency = false;
    if (this.actionsEl) {
      this.actionsEl.hidden = true;
      this.actionsEl.querySelectorAll('[data-pf-choice]').forEach(function (b) {
        b.classList.remove('is-on');
      });
    }
    if (this.fixHelpEl) this.fixHelpEl.hidden = true;
  };

  Panel.prototype.setChoice = function (choice) {
    this.choice = choice;
    this.actionsEl.querySelectorAll('[data-pf-choice]').forEach(function (b) {
      b.classList.toggle('is-on', b.getAttribute('data-pf-choice') === choice);
    });
    if (choice === 'print') {
      this.ackLabel.textContent = this.transparency ? COPY.transparencyAck : COPY.printAck;
      this.ackEl.hidden = false;
      this.fixHelpEl.hidden = true;
    } else if (choice === 'fix') {
      this.ackEl.hidden = true;
      this.ackInput.checked = false;
      this.fixHelpEl.hidden = false;
    } else {
      this.ackEl.hidden = true;
      this.fixHelpEl.hidden = true;
    }
  };

  Panel.prototype.nudge = function () {
    this.nudgeEl.hidden = false;
    this.el.classList.remove('is-flash');
    void this.el.offsetWidth;
    this.el.classList.add('is-flash');
    try { this.el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
    try { this.ackInput.focus({ preventScroll: true }); } catch (e) {}
  };

  /* ======================================================================
     3. ADAPTER  -  observes BAGS, never modifies it
     ====================================================================== */

  /* ------------------------------------------------------------------
     THE ONLY BAGS-SPECIFIC KNOWLEDGE IN THIS FILE.
     Replacing BAGS with a U Press uploader means replacing this object and
     the four helpers below it - the Detector and Panel above know nothing
     about BAGS and never will.
     ------------------------------------------------------------------ */
  var BAGS = {
    fileInput: '#gs-file-input',
    wrapper: '.gs-uploader-wrapper',
    host: '.gs--wrapper',
    cartButton: '#gs-add-cart-btn, .gs-sd-cart-btn',
    dropZone: '#gs-file-drop-zone, #gs-uploader-btn',
    removeButton: '.gs-file-remove',
    // evidence that BAGS has a file in hand, whatever route it arrived by
    uploadEvidence: '.gs-file-container, .gs-sd-upload-card, .gs-sd-detection-result'
  };

  var panel = null;
  var currentKey = null;
  var running = false;
  var pending = null;
  var observer = null;
  var syncTimer = null;

  /* The customer's File is captured the moment it is selected and held here.
     Do NOT go back to input.files later: a drag-and-drop never populates the
     input's FileList at all, and an uploader is free to clear input.value once
     it has read the file. Holding the reference makes us independent of both. */
  var capturedFile = null;
  var lastResult = null;
  var review = null;            // the inspection surface, when open

  var trace = [];
  function mark(tag, extra) {
    if (trace.length < 60) {
      trace.push(Object.assign({ t: Math.round((window.performance && performance.now) ? performance.now() : 0), tag: tag }, extra || {}));
    }
    log(tag, extra || '');
  }

  /* Upload generation. Every time the customer chooses a file - by either route
     - this advances. Two things depend on it:

     STALE RESULTS. Detecting a 245 MP sheet takes seconds. If the customer
     changes their mind mid-detection, the first result must be thrown away, not
     painted over the new artwork. Each run carries the key it started with and
     only applies its result if that key is still current.

     THE SAME FILE TWICE. name+size+lastModified is identical when the customer
     re-picks the very same file, so without the generation the key would not
     change and the detector would silently skip the re-run, leaving the panel
     showing a verdict for artwork that had been removed and re-added. */
  var uploadGen = 0;

  function bumpGen(how) {
    uploadGen++;
    mark('upload-generation', { gen: uploadGen, via: how });
  }

  function fileKey(f) {
    return f ? [f.name, f.size, f.lastModified || 0, uploadGen].join('|') : null;
  }

  function bagsInput() { return document.querySelector(BAGS.fileInput); }
  function bagsWrapper() { return document.querySelector(BAGS.wrapper); }
  function bagsCartBtn() { return document.querySelector(BAGS.cartButton); }
  function isCartTarget(el) { return !!(el && el.closest && el.closest(BAGS.cartButton)); }

  /* Panel sits between BAGS' size/DPI result and its Add to Cart button. If a
     BAGS re-render drops it, the observer puts it back - it is never given a
     job BAGS depends on, so a missing panel can never break ordering. */
  function place() {
    if (!panel) return;
    var wrap = bagsWrapper();
    if (!wrap) return;
    var btn = bagsCartBtn();
    var anchor = (btn && btn.parentElement === wrap) ? btn : null;
    if (anchor) {
      if (panel.el.nextElementSibling !== anchor || panel.el.parentElement !== wrap) {
        wrap.insertBefore(panel.el, anchor);
      }
    } else if (panel.el.parentElement !== wrap) {
      wrap.appendChild(panel.el);
    }
  }

  function teardownPanel() {
    if (panel && panel.el.parentElement) panel.el.parentElement.removeChild(panel.el);
    panel = null;
    currentKey = null;
    mark('panel-removed');
  }

  function run(file, key) {
    if (running) { pending = { file: file, key: key }; return; }
    running = true;
    panel.set('checking');
    mark('decode-start', { name: file.name, size: file.size, type: file.type });
    /* Promise.resolve().then(...) so a SYNCHRONOUS throw inside detect() lands
       in the catch below instead of escaping run() and leaving `running` stuck
       true, which would silently wedge every later upload. */
    Promise.resolve().then(function () { return detect(file); }).then(function (res) {
      running = false;
      if (key !== currentKey) { if (pending) { var p = pending; pending = null; run(p.file, p.key); } return; }
      mark('detector-result', { status: res.status, reason: res.reason, ms: res.ms });
      lastResult = res;
      try { window.__pfLast = { status: res.status, reason: res.reason, ms: res.ms,
        opaqueFrac: res.metrics && res.metrics.opaqueFrac,
        whiteFrac: res.metrics && res.metrics.whiteFrac,
        opaque: res.metrics && res.metrics.opaque,
        scale: res.metrics && res.metrics.scale,
        regions: res.metrics && res.metrics.regionsReported }; } catch (e) {}
      panel.reset();
      panel.areaCount = (res.metrics && res.metrics.regionsSource
                         && res.metrics.regionsSource.length) || 0;
      panel.set(res.status, res.reason);
      panel.el.setAttribute('data-ms', String(res.ms || 0));
      if (pending) { var q = pending; pending = null; run(q.file, q.key); }
    }).catch(function (err) {
      running = false;
      mark('detector-threw', { message: err && err.message });
      if (key !== currentKey) return;
      panel.reset();
      panel.set('unable', 'exception');
    });
  }

  /* Real evidence that a file is in hand - the container must actually be
     rendered AND carry content. Merely existing in the DOM is not enough: some
     of these nodes are present but empty before any upload, and treating that
     as an upload would put a panel on a page with no artwork. */
  function bagsHasUpload() {
    var wrap = bagsWrapper();
    if (!wrap) return false;
    var nodes = wrap.querySelectorAll(BAGS.uploadEvidence);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.offsetHeight > 0 && (el.textContent || '').trim().length > 0) return true;
    }
    return false;
  }

  function sync() {
    var input = bagsInput();
    var file = capturedFile ||
               ((input && input.files && input.files.length) ? input.files[0] : null);

    if (!file) {
      /* No File in hand. If BAGS is clearly holding an upload anyway (e.g. a
         route we could not observe), say so instead of showing nothing - a
         failed integration must never look like "everything is fine". */
      if (bagsHasUpload()) {
        if (!panel) { panel = new Panel(); }
        place();
        if (currentKey !== '__nofile__') {
          currentKey = '__nofile__';
          panel.reset();
          panel.set('unable', 'no-file-reference');
          mark('no-file-but-bags-has-upload');
        }
      } else if (panel) {
        teardownPanel();
      }
      return;
    }

    if (!panel) { panel = new Panel(); panel.set('checking'); }
    place();
    var key = fileKey(file);
    if (key !== currentKey) {
      /* A different file means everything about the previous one is void: the
         warning, the markers, the acknowledgement and the decision. Nothing is
         ever carried forward from artwork the customer has replaced. */
      currentKey = key;
      panel.reset();
      closeReview('new-file');
      run(file, key);
    }
  }

  function scheduleSync() {
    if (syncTimer) return;
    syncTimer = setTimeout(function () { syncTimer = null; try { sync(); } catch (e) { log('sync failed', e); } }, 150);
  }

  function startObserver() {
    var host = document.querySelector(BAGS.host) || document.querySelector('.custom-app-block');
    if (!host || observer) return;
    observer = new MutationObserver(function (records) {
      // ignore mutations we caused ourselves, so this can never self-trigger
      for (var i = 0; i < records.length; i++) {
        var t = records[i].target;
        if (!(panel && panel.el.contains(t))) { scheduleSync(); return; }
      }
    });
    observer.observe(host, { childList: true, subtree: true });
    log('observing BAGS');
  }

  /* ---- the three customer choices --------------------------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-pf-choice]');
    if (!btn || !panel) return;
    var choice = btn.getAttribute('data-pf-choice');
    panel.setChoice(choice);
    mark('customer-choice', { choice: choice });
    if (choice === 'review') {
      openReview();
    } else if (choice === 'fix') {
      // point them at BAGS' own remove control; we never remove it for them
      var rm = document.querySelector(BAGS.removeButton);
      if (rm && rm.scrollIntoView) {
        try { rm.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) {}
      }
    }
  }, false);

  /* repair workspace and BAGS handoff removed for Legacy */

  /* ---- the inspection surface --------------------------------------
     Legacy never repairs. It shows the customer exactly where we found
     something and lets them decide. The surface is a separate module so the
     product page loads none of the repair engine. */
  function openReview() {
    var file = capturedFile;
    var res = lastResult;
    if (!file || !res || !window.upReview) { mark('review-unavailable'); return; }
    var issues = (res.metrics && res.metrics.regionsSource) || [];
    if (!issues.length) { mark('review-no-regions'); return; }
    mark('review-open', { issues: issues.length });
    review = window.upReview.open({
      file: file,
      fileName: file.name,
      sourceW: (res.metrics && res.metrics.sourceW) || 0,
      sourceH: (res.metrics && res.metrics.sourceH) || 0,
      issues: issues,
      copy: COPY,
      onClose: function (why) { review = null; mark('review-closed', { why: why }); },
      onDecision: function (choice) {
        review = null;
        if (panel) { panel.setChoice(choice); mark('customer-choice', { choice: choice, from: 'review' }); }
        if (choice === 'fix') {
          var rm = document.querySelector(BAGS.removeButton);
          if (rm && rm.scrollIntoView) {
            try { rm.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {}
          }
        }
      }
    });
  }

  function closeReview(why) {
    if (review && review.close) { try { review.close(why); } catch (e) {} }
    review = null;
  }

  /* ---- our gate ----------------------------------------------------
     Capture phase at document level so a BAGS re-render of the button cannot
     detach it, and BAGS' own handlers stay untouched. Three ordinary paths a
     customer can take are covered: pointer/tap, keyboard on the button, and
     a form submit. Nothing is ever disabled, so if this module fails to load
     ordering still works exactly as BAGS intends.

     This is assistive, not a security boundary - see the notes in the README
     block at the top of this file. */
  function block(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    panel.nudge();
    return false;
  }

  document.addEventListener('click', function (e) {
    if (!panel || !panel.needsAck()) return;
    if (!isCartTarget(e.target)) return;
    block(e);
  }, true);

  /* Enter/Space on the focused button: stop it here as well, so the
     synthesised click never happens and no BAGS keydown handler can fire. */
  document.addEventListener('keydown', function (e) {
    if (!panel || !panel.needsAck()) return;
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar' && e.keyCode !== 13 && e.keyCode !== 32) return;
    if (!isCartTarget(e.target)) return;
    block(e);
  }, true);

  /* Implicit submit (Enter inside a field) or any programmatic submit of a
     form that contains the uploader. */
  document.addEventListener('submit', function (e) {
    if (!panel || !panel.needsAck()) return;
    var form = e.target;
    if (!form || !form.querySelector) return;
    if (!form.querySelector(BAGS.cartButton) && !form.querySelector(BAGS.wrapper)) return;
    block(e);
  }, true);

  /* Route 1: the native file dialog. Capture phase so we hold the File before
     any uploader handler has had a chance to consume or reset the input. */
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches(BAGS.fileInput)) return;
    var f = (t.files && t.files.length) ? t.files[0] : null;
    if (f) {
      capturedFile = f;
      bumpGen('change');
      mark('file-captured:change', { name: f.name, size: f.size });
    }
    scheduleSync();
  }, true);

  /* Route 2: drag and drop. This never populates the input's FileList, so
     without this listener a dropped file is invisible to us. */
  document.addEventListener('drop', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest(BAGS.dropZone) && !t.closest(BAGS.host)) return;
    var dt = e.dataTransfer;
    var f = (dt && dt.files && dt.files.length) ? dt.files[0] : null;
    if (f) {
      capturedFile = f;
      bumpGen('drop');
      mark('file-captured:drop', { name: f.name, size: f.size });
    }
    scheduleSync();
  }, true);

  /* The customer removing the file is the one case where we forget it. */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest(BAGS.removeButton)) return;
    capturedFile = null;
    currentKey = null;
    bumpGen('remove');
    mark('file-removed');
    scheduleSync();
  }, true);

  window.addEventListener('pagehide', function () {
    if (observer) { observer.disconnect(); observer = null; }
    if (syncTimer) { clearTimeout(syncTimer); syncTimer = null; }
  });

  var booted = false;
  function boot() {
    if (!booted) { booted = true; mark('adapter-mounted'); }
    startObserver();
    scheduleSync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  window.addEventListener('load', boot);

  /* ------------------------------------------------------------------
     Public surface.

     `detect` is the whole detector: give it a File or Blob, get back
     {status, reason, ms, metrics}. It touches no page DOM, knows nothing
     about BAGS, and is what a future U Press uploader would call directly:

         const result = await window.upPreflight.detect(file);

     `renderInto` is here so a future uploader can reuse the same panel UI
     without the BAGS adapter; the adapter simply happens to be the only
     caller today.
     ------------------------------------------------------------------ */
  window.upPreflight = {
    version: 1,
    trace: function () { return trace.slice(); },
    capturedFile: function () {
      return capturedFile ? { name: capturedFile.name, size: capturedFile.size, type: capturedFile.type } : null;
    },
    lastResult: function () { return lastResult; },
    generation: function () { return { gen: uploadGen, currentKey: currentKey,
                                       running: running }; },
    openReview: function () { return openReview(); },
    /* Called when the customer starts another sheet. Everything about the
       previous artwork is dropped: the captured file, the warning, the markers,
       the acknowledgement and the decision. Nothing is ever carried forward. */
    resetForNewSheet: function () {
      capturedFile = null;
      currentKey = null;
      lastResult = null;
      bumpGen('reset-for-new-sheet');
      closeReview('new-sheet');
      if (panel) { panel.reset(); teardownPanel(); }
      mark('reset-for-new-sheet');
    },
    reviewState: function () { return review && review.state ? review.state() : null; },
    detect: detect,                 // File|Blob -> Promise<result>
    config: T,
    copy: COPY,
    createPanel: function () { return new Panel(); },
    state: function () { return panel ? { state: panel.state, acknowledged: !panel.needsAck() } : null; }
  };
})();
